import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiHeart, FiSettings, FiPlay, FiInfo } = FiIcons;

const StripGame = () => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState('setup');
  const [player1, setPlayer1] = useState({ name: '', items: 5, lost: 0 });
  const [player2, setPlayer2] = useState({ name: '', items: 5, lost: 0 });
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [choices, setChoices] = useState({ p1: null, p2: null });
  const [winner, setWinner] = useState(null);
  const [round, setRound] = useState(1);
  const [gameMode, setGameMode] = useState('standard'); // 'standard', 'intense', 'extreme'
  const [showSettings, setShowSettings] = useState(false);
  const [itemCount, setItemCount] = useState(5);
  const [readyForNextRound, setReadyForNextRound] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const gameOptions = [
    { name: 'Búa', emoji: '🔨', beats: 'Kéo' },
    { name: 'Bao', emoji: '📄', beats: 'Búa' },
    { name: 'Kéo', emoji: '✂️', beats: 'Bao' }
  ];

  const startGame = () => {
    if (player1.name && player2.name) {
      setPlayer1({...player1, items: itemCount, lost: 0});
      setPlayer2({...player2, items: itemCount, lost: 0});
      setGameState('playing');
    }
  };

  const makeChoice = (choice) => {
    if (currentPlayer === 1) {
      setChoices({ ...choices, p1: choice });
      setCurrentPlayer(2);
    } else {
      const newChoices = { ...choices, p2: choice };
      setChoices(newChoices);
      setTimeout(() => {
        determineWinner(newChoices);
      }, 1000);
    }
  };

  const determineWinner = (gameChoices) => {
    const p1Choice = gameChoices.p1;
    const p2Choice = gameChoices.p2;

    if (p1Choice.name === p2Choice.name) {
      // Tie - both lose an item
      setPlayer1(prev => ({ ...prev, lost: prev.lost + 1 }));
      setPlayer2(prev => ({ ...prev, lost: prev.lost + 1 }));
      setWinner('tie');
    } else if (p1Choice.beats === p2Choice.name) {
      // Player 1 wins
      setPlayer2(prev => ({ ...prev, lost: prev.lost + 1 }));
      setWinner('player1');
    } else {
      // Player 1 loses
      setPlayer1(prev => ({ ...prev, lost: prev.lost + 1 }));
      setWinner('player2');
    }

    // Wait for player to click "Next Round" button instead of auto-advancing
    setReadyForNextRound(true);
  };

  const nextRound = () => {
    if (player1.lost >= player1.items || player2.lost >= player2.items) {
      setGameState('finished');
      return;
    }

    setChoices({ p1: null, p2: null });
    setCurrentPlayer(1);
    setWinner(null);
    setRound(prev => prev + 1);
    setReadyForNextRound(false);
  };

  const resetGame = () => {
    setPlayer1({ name: player1.name, items: itemCount, lost: 0 });
    setPlayer2({ name: player2.name, items: itemCount, lost: 0 });
    setCurrentPlayer(1);
    setChoices({ p1: null, p2: null });
    setWinner(null);
    setRound(1);
    setGameState('playing');
    setReadyForNextRound(false);
  };

  const getRemainingItems = (player) => {
    return Math.max(0, player.items - player.lost);
  };

  const getStripMessage = (loser) => {
    const standardMessages = [
      `${loser} hãy cởi một món đồ một cách quyến rũ! 😏`,
      `Đến lút ${loser} thực hiện striptease rồi! 💃`,
      `${loser} ơi, đã đến lúc "nhẹ gánh" rồi đấy! 🔥`,
      `${loser} hãy cho chúng ta một màn trình diễn! 😘`,
      `${loser} cởi đồ và nhảy một điệu nhỏ nhé! 💕`
    ];

    const intenseMessages = [
      `${loser} phải cởi một món đồ và hôn nơi nhạy cảm của người thắng! 🔥`,
      `${loser} hãy cởi đồ và tự làm mình thỏa mãn trong 1 phút! 💦`,
      `${loser} phải cởi đồ rồi để người thắng sờ mó khắp nơi trong 2 phút! 😈`,
      `${loser} cởi đồ và uốn éo quyến rũ trong 2 phút! 💋`,
      `${loser} phải cởi đồ và để người thắng thổi nhẹ lên cơ thể! 🌬️`
    ];

    const extremeMessages = [
      `${loser} phải cởi một món đồ và để người thắng làm bất cứ điều gì trong 3 phút! 💦`,
      `${loser} phải cởi đồ rồi tự vuốt ve bộ phận nhạy cảm của mình! 🔞`,
      `${loser} phải cởi đồ và làm theo mọi mệnh lệnh của người thắng trong 5 phút! 😈`,
      `${loser} phải cởi đồ và dùng miệng làm người thắng thỏa mãn trong 2 phút! 👅`,
      `${loser} phải cởi đồ và quỳ xuống phục vụ người thắng! 🧎‍♂️`
    ];

    let messages;
    switch (gameMode) {
      case 'intense':
        messages = intenseMessages;
        break;
      case 'extreme':
        messages = extremeMessages;
        break;
      default:
        messages = standardMessages;
    }

    return messages[Math.floor(Math.random() * messages.length)];
  };

  const getGameModeBadge = () => {
    switch (gameMode) {
      case 'standard':
        return { text: 'Tiêu Chuẩn', color: 'bg-blue-500', emoji: '😊' };
      case 'intense':
        return { text: 'Nóng Bỏng', color: 'bg-orange-500', emoji: '🔥' };
      case 'extreme':
        return { text: 'Cực Kỳ Dâm Đãng', color: 'bg-red-600', emoji: '💦' };
      default:
        return { text: 'Tiêu Chuẩn', color: 'bg-blue-500', emoji: '😊' };
    }
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowPrivacy(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>
            
            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>
              
              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPrivacy(false)}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (gameState === 'setup') {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-4xl font-bold text-white text-center mb-6">
              🔥 Quây Tù Tì Nóng Bỏng 🔥
            </h1>

            <div className="space-y-6">
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Tên Người Chơi 1:
                </label>
                <input
                  type="text"
                  value={player1.name}
                  onChange={(e) => setPlayer1({ ...player1, name: e.target.value })}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                  placeholder="Nhập tên..."
                />
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Tên Người Chơi 2:
                </label>
                <input
                  type="text"
                  value={player2.name}
                  onChange={(e) => setPlayer2({ ...player2, name: e.target.value })}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                  placeholder="Nhập tên..."
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-pink-200 text-sm font-bold">
                    Chế Độ Chơi:
                  </label>
                  <div className={`${getGameModeBadge().color} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {getGameModeBadge().emoji} {getGameModeBadge().text}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button 
                    className={`p-2 rounded-xl text-sm font-medium transition-all ${gameMode === 'standard' ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                    onClick={() => setGameMode('standard')}
                  >
                    Tiêu Chuẩn 😊
                  </button>
                  <button 
                    className={`p-2 rounded-xl text-sm font-medium transition-all ${gameMode === 'intense' ? 'bg-orange-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                    onClick={() => setGameMode('intense')}
                  >
                    Nóng Bỏng 🔥
                  </button>
                  <button 
                    className={`p-2 rounded-xl text-sm font-medium transition-all ${gameMode === 'extreme' ? 'bg-red-600 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                    onClick={() => setGameMode('extreme')}
                  >
                    Cực Dâm 💦
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Số Món Đồ ({itemCount}):
                </label>
                <input
                  type="range"
                  min="3"
                  max="10"
                  value={itemCount}
                  onChange={(e) => setItemCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-pink-200 mt-1">
                  <span>3</span>
                  <span>5</span>
                  <span>7</span>
                  <span>10</span>
                </div>
              </div>

              <div className="bg-pink-500/20 p-4 rounded-xl">
                <h3 className="text-white font-medium mb-2 text-center">Hướng Dẫn Chơi</h3>
                <p className="text-pink-200 text-sm">
                  1. Mỗi người chơi lần lượt chọn Búa (thắng Kéo), Bao (thắng Búa) hoặc Kéo (thắng Bao).<br/>
                  2. Người thua sẽ phải cởi một món đồ và thực hiện thử thách theo chế độ chơi.<br/>
                  3. Nếu hòa, cả hai đều phải cởi đồ.<br/>
                  4. Người nào cởi hết đồ trước sẽ thua cuộc!
                </p>
                <p className="text-pink-200 text-sm mt-2 text-center">
                  {gameMode === 'standard' && 'Chế độ Tiêu Chuẩn: Thử thách nhẹ nhàng và vui vẻ 😊'}
                  {gameMode === 'intense' && 'Chế độ Nóng Bỏng: Thử thách gợi cảm và quyến rũ 🔥'}
                  {gameMode === 'extreme' && 'Chế độ Cực Kỳ Dâm Đãng: Thử thách cực kỳ táo bạo và dâm đãng 💦'}
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startGame}
                disabled={!player1.name || !player2.name}
                className="w-full bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-2xl shadow-lg transition-all duration-300"
              >
                Bắt Đầu Trò Chơi! 🔥
              </motion.button>

              <div className="flex items-center justify-between mt-4">
                <button 
                  onClick={() => setShowPrivacy(true)}
                  className="text-pink-200 text-xs underline flex items-center"
                >
                  <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
                  Điều khoản & Quyền riêng tư
                </button>
                <p className="text-pink-200 text-xs">
                  © 2023 Lux Homestay
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    const finalWinner = getRemainingItems(player1) > getRemainingItems(player2) ? player1 : player2;
    const loser = finalWinner === player1 ? player2 : player1;

    return (
      <div className="min-h-screen p-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl text-center"
        >
          <h1 className="text-4xl font-bold text-white mb-6">
            🎉 Trò Chơi Kết Thúc! 🎉
          </h1>

          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-2xl mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              🏆 {finalWinner.name} Thắng! 🏆
            </h2>
            <p className="text-white/90">
              Còn lại {getRemainingItems(finalWinner)} món đồ
            </p>
          </div>

          <div className="bg-red-500/20 p-4 rounded-xl mb-6">
            <p className="text-pink-200 text-lg">
              {getStripMessage(loser.name)}
            </p>
          </div>

          <div className="space-y-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetGame}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiRefreshCw} />
              Chơi Lại
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300"
            >
              Về Menu Chính
            </motion.button>
          </div>

          <div className="flex items-center justify-between mt-6">
            <button 
              onClick={() => setShowPrivacy(true)}
              className="text-pink-200 text-xs underline flex items-center"
            >
              <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
              Điều khoản & Quyền riêng tư
            </button>
            <p className="text-pink-200 text-xs">
              © 2023 Lux Homestay
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
      >
        <SafeIcon icon={FiArrowLeft} className="text-xl" />
      </motion.button>

      <div className="flex-1 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <h1 className="text-3xl font-bold text-white">
              Round {round}
            </h1>
            <div className={`${getGameModeBadge().color} text-white text-xs font-bold px-3 py-1 rounded-full`}>
              {getGameModeBadge().emoji} {getGameModeBadge().text}
            </div>
          </div>
          
          <div className="flex justify-center gap-8 text-white">
            <div className={`text-center p-4 rounded-xl ${currentPlayer === 1 ? 'bg-pink-500/30' : 'bg-white/10'}`}>
              <h3 className="font-bold text-lg">{player1.name}</h3>
              <p className="text-sm">Còn lại: {getRemainingItems(player1)} món</p>
            </div>
            <div className={`text-center p-4 rounded-xl ${currentPlayer === 2 ? 'bg-pink-500/30' : 'bg-white/10'}`}>
              <h3 className="font-bold text-lg">{player2.name}</h3>
              <p className="text-sm">Còn lại: {getRemainingItems(player2)} món</p>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {!choices.p1 || !choices.p2 ? (
            <motion.div
              key="choice"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="max-w-md w-full"
            >
              <div className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl text-center">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {currentPlayer === 1 ? player1.name : player2.name}, chọn đi! 
                  {currentPlayer === 2 && ' 🙈'}
                </h2>
                
                {currentPlayer === 2 && (
                  <p className="text-pink-200 mb-4 text-sm">
                    {player1.name} đã chọn rồi, đến lượt bạn!
                  </p>
                )}

                <div className="grid grid-cols-3 gap-4">
                  {gameOptions.map((option) => (
                    <motion.button
                      key={option.name}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => makeChoice(option)}
                      className="bg-gradient-to-br from-pink-400 to-purple-500 hover:from-pink-500 hover:to-purple-600 p-6 rounded-2xl text-white font-bold shadow-lg transition-all duration-300"
                    >
                      <div className="text-4xl mb-2">{option.emoji}</div>
                      <div className="text-lg">{option.name}</div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="max-w-md w-full"
            >
              <div className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl text-center">
                <h2 className="text-2xl font-bold text-white mb-6">Kết Quả!</h2>
                
                <div className="flex justify-center gap-8 mb-6">
                  <div className="text-center">
                    <h3 className="text-white font-bold mb-2">{player1.name}</h3>
                    <div className="text-6xl mb-2">{choices.p1.emoji}</div>
                    <p className="text-pink-200">{choices.p1.name}</p>
                  </div>
                  <div className="flex items-center text-4xl">⚡</div>
                  <div className="text-center">
                    <h3 className="text-white font-bold mb-2">{player2.name}</h3>
                    <div className="text-6xl mb-2">{choices.p2.emoji}</div>
                    <p className="text-pink-200">{choices.p2.name}</p>
                  </div>
                </div>

                {winner && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl ${
                      winner === 'tie' 
                        ? 'bg-yellow-500/20' 
                        : winner === 'player1' 
                          ? 'bg-green-500/20' 
                          : 'bg-red-500/20'
                    }`}
                  >
                    {winner === 'tie' ? (
                      <p className="text-yellow-200 font-bold">
                        🤝 Hòa! Cả hai đều phải cởi một món đồ! 😈
                      </p>
                    ) : (
                      <p className="text-white font-bold">
                        🎉 {winner === 'player1' ? player1.name : player2.name} thắng!
                        <br />
                        <span className="text-pink-200">
                          {getStripMessage(winner === 'player1' ? player2.name : player1.name)}
                        </span>
                      </p>
                    )}
                  </motion.div>
                )}
                
                {readyForNextRound && (
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={nextRound}
                    className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiPlay} />
                    Vòng Tiếp Theo
                  </motion.button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="w-full max-w-md mt-6">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setShowPrivacy(true)}
              className="text-pink-200 text-xs underline flex items-center"
            >
              <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
              Điều khoản & Quyền riêng tư
            </button>
            <p className="text-pink-200 text-xs">
              © 2023 Lux Homestay
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripGame;